/**
 * The gear the planner recommends in ca_preview: the same merchandise the
 * store leads with (its homepage rail is Dometic, myCOOLMAN, Kamado Joe),
 * picked per kind of trip. Live price, stock and images come from the store
 * itself (/products/<handle>.js); a handle that is gone or sold out simply
 * drops out of the rail.
 */

export type GearContext = 'general' | 'caravan' | 'free' | 'stay';

export const GEAR_HANDLES: Record<GearContext, string[]> = {
  general: [
    'dometic-cfx5-45-portable-fridge-freezer',
    'dometic-go-compact-camp-shelter',
    'mycoolman-energy-solar-blanket-100w',
    'dometic-go-compact-camp-chair-1',
    'kamado-joe-jr-bbq-grill',
    'dometic-go-hydration-water-jug-11l-slate',
    'mycoolman-60-can-insulated-cooler',
    'dometic-go-compact-bamboo-camp-table-100l-x-65w',
  ],
  caravan: [
    'dometic-nrx-115c-upright-compressor-fridge-freezer',
    'mycoolman-energy-power-station-1008wh-78ah',
    'dometic-8700-awning',
    'mycoolman-55-recreational-series-dual-zone',
    'dometic-go-compact-camp-chair-1',
    'kamado-joe-jr-bbq-grill',
    'dometic-go-compact-bamboo-camp-table-100l-x-65w',
    'mycoolman-energy-solar-blanket-200w',
  ],
  free: [
    'mycoolman-energy-power-station-1008wh-78ah',
    'dometic-cfx5-35-portable-fridge-freezer',
    'mycoolman-energy-solar-blanket-100w',
    'dometic-go-hydration-water-jug-11l-slate',
    '1-person-inflatable-swag-pico-ftc-1x1-tc-by-dometic',
    'dometic-go-compact-camp-chair-1',
    'mycoolman-power-pack-15ah-lifepo4-ccp15',
    'dometic-patrol-20-outdoor-cooler-insulated-ice-chest-18-8-litre',
  ],
  stay: [
    'dometic-go-compact-camp-chair-1',
    'mycoolman-36-can-hardbody-duffel-cooler',
    'dometic-go-hydration-water-jug-11l-slate',
    'kamado-joe-jr-bbq-grill',
    'mycoolman-30-can-insulated-sport-tote-25l',
    'dometic-thermo-bottle-660ml',
    'dometic-go-compact-bamboo-camp-table-100l-x-65w',
    'dometic-patrol-20-outdoor-cooler-insulated-ice-chest-18-8-litre',
  ],
};

/** Which rail a campsite gets, from its entity type. */
export function gearContextFor(entityType?: string | null): GearContext {
  const t = (entityType || '').toLowerCase();
  if (t === 'caravan_park' || t === 'caravan') return 'caravan';
  if (t === 'free_camping') return 'free';
  if (t === 'accom' || t === 'accommodation' || t === 'tours') return 'stay';
  return 'general';
}

/**
 * The store's display name for a supplier title (theme snippet
 * aaa-display-name, part 'name'): only a title ending "by <vendor>" changes.
 *   "2 Person 3-Season Hiking Tent - Vern 2 3S - 2.5kg by Nortent" -> "Nortent Vern 2 3S"
 *   "Fridge Slide With Cutting Board by BOAB" -> "Fridge Slide With Cutting Board"
 */
export function storeDisplayName(title: string, vendor?: string | null): string {
  const t = (title || '').trim();
  const v = (vendor || '').trim();
  if (!v) return t;
  const by = ` by ${v}`.toLowerCase();
  if (t.length <= by.length || !t.toLowerCase().endsWith(by)) return t;
  const core = t.slice(0, t.length - by.length).trim();
  const segs = core.split(' - ');
  if (segs.length < 2) return core;
  const isWeight = (s: string) => /^[0-9][0-9.\s]*(kg|g)$/i.test(s.trim());
  const model = segs
    .slice(1)
    .map((s) => s.trim())
    .filter((s) => s && !isWeight(s))
    .join(' ');
  if (!model) return segs[0].trim();
  return model.toLowerCase().includes(v.toLowerCase()) ? model : `${v} ${model}`;
}

export interface GearItem {
  handle: string;
  title: string;
  vendor: string;
  image: string | null;
  /** dollars */
  price: number;
  compareAt: number | null;
  priceVaries: boolean;
  variantId: number | null;
  singleVariant: boolean;
  available: boolean;
}

/** Shape a /products/<handle>.js body into a card. */
export function toGearItem(p: any): GearItem | null {
  if (!p || !p.handle) return null;
  const variants: any[] = Array.isArray(p.variants) ? p.variants : [];
  const first = variants.find((v) => v.available) || variants[0];
  const img = p.featured_image || (Array.isArray(p.images) && p.images[0]) || null;
  const price = typeof p.price === 'number' ? p.price / 100 : NaN;
  if (!Number.isFinite(price) || !img) return null;
  const cmp = typeof p.compare_at_price === 'number' ? p.compare_at_price / 100 : null;
  return {
    handle: p.handle,
    title: storeDisplayName(p.title || '', p.vendor),
    vendor: p.vendor || '',
    image: typeof img === 'string' ? (img.startsWith('//') ? `https:${img}` : img) : null,
    price,
    // A saving is only claimed when the price doesn't vary by variant (as the store does).
    compareAt: !p.price_varies && cmp !== null && cmp > price ? cmp : null,
    priceVaries: !!p.price_varies,
    variantId: first?.id ?? null,
    singleVariant: variants.length <= 1,
    available: !!p.available,
  };
}

export function money(n: number): string {
  return n.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: n % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}
